import { Modal, Form, Input, Button, Select, Row, Col, Checkbox } from "antd";
import Draggable from "react-draggable";
import { useLocation } from "react-router-dom";
import { formItemLayout, inputLayout2 } from "../../../../constants/ApplicationConstants/FormConstants";
import CircularProgressBar from "../../../common/CircularProgressBar";
import Loading from "../../../common/Loading";
import FormInputRules from "../../../../util/FormInputRules";
import { General } from "../../../../util/General";
import useKeyFobAllocationMaster from "./index.hook";
import { createHandleKeyPress } from "../../../../util/handleKeyPress";
import styles from "../../index.module.less";
import { useReactDraggable } from "../../../../hooks/useReactDraggable";

import type FixMeLater from "../../../../shared/types/fixMeLater.type";
import CustomerClientKeyFobStats from "./CustomerClientKeyFobStats";

type KeyFobAllocationMasterProps = {
  saving: boolean;
  addRecord: boolean;
  handleAddRecord: (value: boolean) => void;
  rowData: FixMeLater;
  handleClearRowIdAndActionType: () => void;
  handleSubmit: (data: FixMeLater) => void;
};

const KeyFobAllocationMaster = (props: KeyFobAllocationMasterProps) => {
  /* CONSTANTS */
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isVisitor = queryParams.get("isVisitor");

  const config = useKeyFobAllocationMaster(props);

  const { bounds, onStart } = useReactDraggable();

  return (
    <>
      <Modal
        afterClose={() => config.form.resetFields()}
        destroyOnClose={true}
        maskClosable={false}
        centered
        title={<div className="modal-container">{`${props.addRecord ? "Add" : "Edit"} KeyFob Allocation Master`}</div>}
        open={true}
        onCancel={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}
        width={"80%"}
        okButtonProps={{
          style: {
            display: "none"
          }
        }}
        cancelButtonProps={{ style: { display: "none" } }}
        modalRender={(modal) => (
          <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
            {modal}
          </Draggable>
        )}
      >
        {((props.addRecord && config.isOnAddLoading) || (!props.addRecord && !config.formFieldsLoadedToForm)) && (
          <div className={`${styles["textCenter"]}`}>
            <Loading />
          </div>
        )}

        {((props.addRecord && !config.isOnAddLoading) || (!props.addRecord && config.formFieldsLoadedToForm)) && (
          <Form
            ref={config.formRef}
            onValuesChange={(changedValues) => {
              const fieldName = Object.keys(changedValues)[0];
              config.form.validateFields([fieldName]);
            }}
            className={`${styles["formDiv"]} ${styles["formDivExtended"]}`}
            {...formItemLayout}
            form={config.form}
            validateTrigger="onBlur"
            colon={false}
            name="register"
            onFinish={config.handleFinish}
            initialValues={{ has_add_edit_access: false, is_visitor: isVisitor ? true : false }}
          >
            <Row gutter={24}>
              <Col span={12} xs={24} sm={24} md={24} lg={12}>
                <Form.Item {...inputLayout2} name="customer_client_id" label="Client">
                  <Select className={`${styles["from-Item-selectDiv"]}`} onChange={config.handleChangeClient} listHeight={200} disabled={!props.addRecord} showSearch allowClear getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} placeholder="Select client">
                    {config.clientList?.map((item: { id: number; client_name: string }) => (
                      <Select.Option key={item.id} value={item.id}>
                        {item.client_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                {Boolean(config.customerClientId) && (
                  <Form.Item {...inputLayout2} name="customer_client_id" label={<div style={{ paddingTop: "3rem" }}>KeyFob Status</div>}>
                    <CustomerClientKeyFobStats customerClientId={config.customerClientId} handleCustomerClientId={config.handleCustomerClientId} />
                  </Form.Item>
                )}

                <Form.Item {...inputLayout2} name="key_fob_id" label="KeyFob Token" rules={[FormInputRules.requiredFieldSelectRule("key fob token")]}>
                  <Select className={`${styles["from-Item-selectDiv"]}`} disabled={!props.addRecord} showSearch listHeight={200} allowClear getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} placeholder="Select KeyFob token">
                    {config.keyFobList?.map((item) => (
                      <Select.Option key={item.id} value={item.id}>
                        {item?.keyFob_vehicle_dimension}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                {config.customerClientId && (
                  <Form.Item {...inputLayout2} name="employee_id" label="Employee Id" normalize={General.inputWhiteSpaceRemove}>
                    <Input name="employee_id" disabled={!props.addRecord} />
                  </Form.Item>
                )}

                {config.isVisitorChecked && (
                  <Form.Item {...inputLayout2} name="vehicle_number" label="Vehicle Number" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.validateVehicleNumber()]}>
                    <Input name="vehicle_number" disabled={!props.addRecord} />
                  </Form.Item>
                )}
              </Col>

              <Col span={12} xs={24} sm={24} md={24} lg={12}>
                <Form.Item {...inputLayout2} name="mobile_number" label="Mobile Number" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("contact number"), props.addRecord ? FormInputRules.ContactNumberValidation2Rule() : FormInputRules.ContactNumberValidationRule()]}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Input prefix={props.addRecord ? "+91" : ""} name="mobile_number" defaultValue={config.mobileNumber ?? ""} onKeyPress={createHandleKeyPress()} onChange={(e) => config.handleChangeNumber(e.target.value)} disabled={!props.addRecord} />
                    {props.addRecord && (
                      <Button className="gx-mb-0 button-gradiant" onClick={config.verifyMobileNumber} style={{ marginLeft: "10px" }}>
                        Verify
                      </Button>
                    )}
                  </div>
                </Form.Item>

                <Form.Item {...inputLayout2} name="first_name" label="First name" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("first name"), FormInputRules.ValidateFirstAndLastName()]}>
                  <Input name="first_name" disabled={config.userDetails?.["first_name"]} />
                </Form.Item>

                <Form.Item {...inputLayout2} name="last_name" label="Last Name" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("last name"), FormInputRules.ValidateFirstAndLastName()]}>
                  <Input name="last_name" disabled={config.userDetails?.["last_name"]} />
                </Form.Item>

                <Form.Item {...inputLayout2} name="is_visitor" valuePropName="checked" label="Is Visitor">
                  <Checkbox disabled={!props.addRecord} checked={config.isVisitorChecked} onChange={(e) => config.handleCheckboxChange(e.target.checked)} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item className={`${styles["form-itemFloatR20"]}`}>
              <Button className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`} htmlType="submit">
                {props.saving ? <CircularProgressBar /> : props.addRecord && config.isVisitorChecked ? "Submit & Send Booking Request" : props.addRecord ? "Submit" : "Update"}
              </Button>
            </Form.Item>

            <Form.Item className={`${styles["form-itemFloatR20"]}`}>
              <Button disabled={props.saving} className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`} onClick={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}>
                Close
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default KeyFobAllocationMaster;
